import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { openPhpMyAdmin } from "../lib/pma";
import type {
  Health,
  HostPlan,
  LocalLicenseStatus,
  PackagePlan,
  PanelUser,
  ResellerProfile,
  ServerMetrics,
  Site
} from "../types";
import type { NavKey } from "../types";
import {
  loadFavorites,
  sectionsForRole,
  toolsForRole,
  type WhmSectionId,
  type WhmTool
} from "../whmTools";

/**
 * Premium 2026 WHM Home — health, capacity, quick actions, accounts, alerts.
 * Spec: upgrade.md
 */
export function WhmHomePage({
  user,
  health,
  os,
  agent,
  meta,
  stats,
  packages: _packages = [],
  hostPlans = [],
  resellerProfile,
  accounts = [],
  sites = [],
  sitesCount = 0,
  databasesCount = 0,
  licenseStatus,
  searchQuery = "",
  onRefresh,
  onNav,
  onOpenLicenses,
  onLicenseRefresh,
  initialSection: _initialSection,
  authorityMode = "master"
}: {
  user: PanelUser;
  health: Health | null;
  os: Record<string, string> | null;
  agent: Record<string, unknown> | null;
  meta: { phase?: number; version?: string } | null;
  stats: {
    total: number;
    active: number;
    suspended: number;
    admins: number;
    provision?: { ready: number; failed: number; pending: number };
  } | null;
  packages?: PackagePlan[];
  hostPlans?: HostPlan[];
  resellerProfile?: ResellerProfile | null;
  accounts?: PanelUser[];
  sites?: Site[];
  sitesCount?: number;
  databasesCount?: number;
  licenseStatus?: LocalLicenseStatus | null;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  onRefresh: () => Promise<void>;
  onNav: (
    k: NavKey,
    opts?: {
      createAccount?: boolean;
      serverConfigTab?:
        | "status"
        | "basic"
        | "tweaks"
        | "php"
        | "metrics"
        | "panel";
      advancedTab?:
        | "overview"
        | "cluster"
        | "limits"
        | "integrations"
        | "licenses"
        | "appearance";
      toolId?: string;
    }
  ) => void;
  onOpenLicenses?: () => void;
  /** Customer panel: force re-pull license from Master Owner Panel */
  onLicenseRefresh?: () => Promise<void> | void;
  initialSection?: WhmSectionId;
  authorityMode?: "master" | "customer";
}) {
  void _packages;
  void _initialSection;

  const agentOk = health?.checks?.agent?.status === "ok";
  const isReseller = user.role === "reseller";
  const tools = useMemo(
    () => toolsForRole(user.role, authorityMode),
    [user.role, authorityMode]
  );
  const q = searchQuery;
  const [favs] = useState<string[]>(() => loadFavorites(user.role));
  const [metrics, setMetrics] = useState<ServerMetrics | null>(null);
  const [acctQ, setAcctQ] = useState("");
  const [licRefreshBusy, setLicRefreshBusy] = useState(false);
  const [licRefreshMsg, setLicRefreshMsg] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "suspended">(
    "all"
  );

  const doLicenseRefresh = async () => {
    if (!onLicenseRefresh || licRefreshBusy) return;
    setLicRefreshBusy(true);
    setLicRefreshMsg("");
    try {
      await onLicenseRefresh();
      setLicRefreshMsg("License status updated");
      window.setTimeout(() => setLicRefreshMsg(""), 3000);
    } catch (err) {
      setLicRefreshMsg(
        err instanceof Error ? err.message : "Refresh failed"
      );
    } finally {
      setLicRefreshBusy(false);
    }
  };

  const loadMetrics = useCallback(async () => {
    if (isReseller) return;
    try {
      const m = await api<{ metrics: ServerMetrics }>("/api/v1/metrics");
      setMetrics(m.metrics || null);
    } catch {
      setMetrics(null);
    }
  }, [isReseller]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  const openTool = (t: WhmTool) => {
    if (t.id === "phpmyadmin") {
      void openPhpMyAdmin({ serverWide: true }).catch(() => {
        onNav("databases", { toolId: t.id });
      });
      return;
    }
    if (t.id === "create-account" || t.hint === "create") {
      onNav("accounts", { createAccount: true, toolId: t.id });
      return;
    }
    if (
      t.hint === "status" ||
      t.hint === "basic" ||
      t.hint === "tweaks" ||
      t.hint === "php" ||
      t.hint === "metrics"
    ) {
      onNav(t.nav, { serverConfigTab: t.hint, toolId: t.id });
      return;
    }
    if (
      t.hint === "overview" ||
      t.hint === "cluster" ||
      t.hint === "limits" ||
      t.hint === "integrations" ||
      t.hint === "licenses" ||
      t.hint === "appearance"
    ) {
      onNav(t.nav, { advancedTab: t.hint, toolId: t.id });
      return;
    }
    onNav(t.nav, { toolId: t.id });
  };

  const totalUsers = isReseller
    ? resellerProfile?.usage?.accounts ?? 0
    : stats?.total ?? accounts.filter((a) => a.role === "user").length;
  const totalDomains = isReseller
    ? resellerProfile?.usage?.domains ?? sitesCount
    : sitesCount || sites.length;
  const totalDbs = isReseller
    ? resellerProfile?.usage?.databases ?? databasesCount
    : databasesCount;

  const seatMax =
    licenseStatus?.maxAccounts ??
    (isReseller ? resellerProfile?.maxAccounts : null) ??
    5000;
  const seatUsed = licenseStatus?.usedAccounts ?? totalUsers;
  const seatAvail =
    licenseStatus?.availableAccounts ??
    Math.max(0, (seatMax || 0) - seatUsed);
  const seatPct =
    seatMax && seatMax > 0
      ? Math.min(100, Math.round((seatUsed / seatMax) * 100))
      : 0;

  const cpuPct = metrics?.cpu?.usagePercent ?? null;
  const ramPct = metrics?.memory?.usedPercent ?? null;
  const diskPct =
    metrics?.disk?.[0]?.usedPercent != null
      ? metrics.disk[0].usedPercent
      : null;

  const loadAvg = metrics?.load
    ? `${fmtNum(metrics.load.one)} · ${fmtNum(metrics.load.five)} · ${fmtNum(metrics.load.fifteen)}`
    : null;

  const healthLabel =
    !agentOk && !isReseller
      ? "Degraded"
      : cpuPct != null && cpuPct > 90
        ? "Critical"
        : diskPct != null && diskPct > 90
          ? "Critical"
          : cpuPct != null && cpuPct > 75
            ? "Warning"
            : "Healthy";

  const healthTone =
    healthLabel === "Healthy"
      ? "ok"
      : healthLabel === "Warning"
        ? "warn"
        : "crit";

  const alerts = useMemo(() => {
    const list: {
      level: "critical" | "warning" | "info";
      title: string;
      detail: string;
      action?: () => void;
      actionLabel?: string;
    }[] = [];

    if (!agentOk && !isReseller) {
      list.push({
        level: "critical",
        title: "Hosting engine offline",
        detail:
          health?.checks?.agent?.detail ||
          "Restart realpanel-agent to provision accounts.",
        action: () => void onRefresh(),
        actionLabel: "Recheck"
      });
    }
    if (diskPct != null && diskPct >= 80) {
      list.push({
        level: diskPct >= 90 ? "critical" : "warning",
        title: `Disk usage at ${Math.round(diskPct)}%`,
        detail: "Free space before backups and account growth fail.",
        action: () => onNav("metrics", { serverConfigTab: "status" }),
        actionLabel: "View metrics"
      });
    }
    if (seatMax && seatPct >= 90) {
      list.push({
        level: seatPct >= 98 ? "critical" : "warning",
        title: "Account capacity nearly full",
        detail: `${seatUsed.toLocaleString()} / ${seatMax.toLocaleString()} seats used.`,
        action: onOpenLicenses,
        actionLabel: "View license"
      });
    }
    if (
      licenseStatus?.daysRemaining != null &&
      licenseStatus.daysRemaining <= 30 &&
      licenseStatus.daysRemaining >= 0
    ) {
      list.push({
        level: licenseStatus.daysRemaining <= 7 ? "critical" : "warning",
        title: `License expires in ${licenseStatus.daysRemaining} day(s)`,
        detail: licenseStatus.expiresAt
          ? `Expires ${formatDate(licenseStatus.expiresAt)}`
          : "Renew to avoid interruption.",
        action: onOpenLicenses,
        actionLabel: "View license"
      });
    }
    if (!licenseStatus?.valid && licenseStatus) {
      list.push({
        level: "warning",
        title: "License not fully active",
        detail: licenseStatus.message || licenseStatus.status || "Check license status.",
        action: onOpenLicenses,
        actionLabel: "View license"
      });
    }
    const expiringSsl = sites.filter((s) => {
      if (!s.sslExpiresAt) return false;
      const days =
        (new Date(s.sslExpiresAt).getTime() - Date.now()) / 86400000;
      return days >= 0 && days <= 14;
    });
    if (expiringSsl.length) {
      list.push({
        level: "warning",
        title: `${expiringSsl.length} SSL certificate(s) expiring soon`,
        detail: expiringSsl
          .slice(0, 3)
          .map((s) => s.domain)
          .join(", "),
        action: () => onNav("ssl"),
        actionLabel: "SSL/TLS"
      });
    }
    if (stats?.suspended) {
      list.push({
        level: "info",
        title: `${stats.suspended} suspended account(s)`,
        detail: "Review suspended users under Account Functions.",
        action: () => onNav("accounts"),
        actionLabel: "List accounts"
      });
    }
    return list;
  }, [
    agentOk,
    isReseller,
    health,
    diskPct,
    seatMax,
    seatPct,
    seatUsed,
    licenseStatus,
    sites,
    stats,
    onRefresh,
    onNav,
    onOpenLicenses
  ]);

  const hostAccounts = useMemo(() => {
    let list = accounts.filter(
      (a) => a.role === "user" && a.status !== "deleted"
    );
    if (statusFilter === "active")
      list = list.filter((a) => a.status === "active");
    if (statusFilter === "suspended")
      list = list.filter((a) => a.status === "suspended");
    const s = acctQ.trim().toLowerCase();
    if (s) {
      list = list.filter(
        (a) =>
          a.username.toLowerCase().includes(s) ||
          (a.primaryDomain || "").toLowerCase().includes(s) ||
          (a.packageName || "").toLowerCase().includes(s) ||
          (a.email || "").toLowerCase().includes(s)
      );
    }
    return list.slice(0, 8);
  }, [accounts, acctQ, statusFilter]);

  const domainByUser = useMemo(() => {
    const m = new Map<string, string>();
    for (const site of sites) {
      if (site.type === "primary" && site.userId) {
        m.set(site.userId, site.domain);
      }
    }
    return m;
  }, [sites]);

  const quickActions = useMemo(() => {
    const defaults: {
      id: string;
      title: string;
      desc: string;
      run: () => void;
    }[] = [
      {
        id: "create-account",
        title: "Create Account",
        desc: "Provision a new hosting account",
        run: () => onNav("accounts", { createAccount: true })
      },
      {
        id: "list-accounts",
        title: "List Accounts",
        desc: "View and manage all accounts",
        run: () => onNav("accounts")
      },
      {
        id: "hosting-plans",
        title: "Create Hosting Plan",
        desc: "Define resources for customers",
        run: () => onNav("plans", { toolId: "hosting-plans" })
      },
      {
        id: "add-domain",
        title: "Add Domain",
        desc: "Attach a domain to an account",
        run: () => onNav("sites", { toolId: "add-domain" })
      },
      {
        id: "mysql",
        title: "Create Database",
        desc: "MySQL / MariaDB databases",
        run: () => onNav("databases")
      },
      {
        id: "phpmyadmin",
        title: "phpMyAdmin",
        desc: "Open database manager",
        run: () => {
          void openPhpMyAdmin({ serverWide: true }).catch(() =>
            onNav("databases")
          );
        }
      }
    ];
    // Prefer favorites when set
    if (favs.length) {
      const fromFav = favs
        .map((id) => tools.find((t) => t.id === id))
        .filter(Boolean) as WhmTool[];
      if (fromFav.length) {
        return fromFav.slice(0, 6).map((t) => ({
          id: t.id,
          title: t.label,
          desc: t.description,
          run: () => openTool(t)
        }));
      }
    }
    return defaults;
  }, [favs, tools, onNav]);

  const searching = q.trim().length > 0;
  const filteredTools = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return tools.filter(
      (t) =>
        t.label.toLowerCase().includes(s) ||
        t.description.toLowerCase().includes(s) ||
        t.id.includes(s)
    );
  }, [tools, q]);

  const planLabel =
    licenseStatus?.plan ||
    licenseStatus?.skuName ||
    (isReseller ? "Reseller" : "License");

  return (
    <div className="rp-dash">
      {/* Page title row */}
      <div className="rp-dash-head">
        <div>
          <h1 className="rp-dash-title">Overview</h1>
          <p className="rp-dash-sub">
            Server health, capacity, and what needs attention
          </p>
        </div>
        <div className="rp-dash-head-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              void onRefresh();
              void loadMetrics();
            }}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => onNav("accounts", { createAccount: true })}
          >
            + Create Account
          </button>
        </div>
      </div>

      {/* License status card (compact) */}
      <section className="rp-lic-card">
        <div className="rp-lic-main">
          <div className="rp-lic-title-row">
            <h2>{planLabel || "RealPanel"}</h2>
            <StatusPill
              tone={
                licenseStatus?.valid
                  ? "ok"
                  : licenseStatus?.inGrace
                    ? "warn"
                    : "muted"
              }
              label={
                licenseStatus?.valid
                  ? "Active"
                  : licenseStatus?.status || "Unknown"
              }
            />
          </div>
          <p className="rp-lic-meta">
            {seatUsed.toLocaleString()} /{" "}
            {(seatMax || 0).toLocaleString()} accounts ·{" "}
            {seatAvail.toLocaleString()} available
            {licenseStatus?.expiresAt
              ? ` · Expires ${formatDate(licenseStatus.expiresAt)}`
              : ""}
            {licenseStatus?.skuName
              ? ` · ${licenseStatus.skuName}`
              : ""}
          </p>
          {licRefreshMsg ? (
            <p className="rp-lic-meta" style={{ marginTop: 6, color: "var(--mint, #16a36a)" }}>
              {licRefreshMsg}
            </p>
          ) : null}
          <div className="rp-progress">
            <div
              className="rp-progress-fill"
              style={{ width: `${seatPct}%` }}
            />
          </div>
        </div>
        <div className="rp-lic-actions">
          {onLicenseRefresh ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={licRefreshBusy}
              onClick={() => void doLicenseRefresh()}
              title="Pull latest license from the RealPanel Owner panel"
            >
              {licRefreshBusy ? "Refreshing…" : "License Refresh"}
            </button>
          ) : null}
          {onOpenLicenses ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onOpenLicenses}
            >
              View License
            </button>
          ) : null}
        </div>
      </section>

      {/* KPI strip */}
      <div className="rp-kpi-row">
        <Kpi
          label="Accounts"
          value={`${seatUsed.toLocaleString()} / ${(seatMax || "∞").toLocaleString()}`}
          hint={`${seatAvail.toLocaleString()} available`}
          onClick={() => onNav("accounts")}
        />
        <Kpi
          label="Domains"
          value={String(totalDomains)}
          hint="Hosted sites"
          onClick={() => onNav("sites")}
        />
        <Kpi
          label="Databases"
          value={String(totalDbs)}
          hint="SQL services"
          onClick={() => onNav("databases")}
        />
        <Kpi
          label="License"
          value={
            licenseStatus?.daysRemaining != null
              ? `${licenseStatus.daysRemaining}d`
              : licenseStatus?.valid
                ? "Active"
                : "—"
          }
          hint={
            licenseStatus?.daysRemaining != null
              ? "days remaining"
              : planLabel || "status"
          }
          onClick={onOpenLicenses}
        />
      </div>

      {/* Health + Quick actions */}
      <div className="rp-dash-mid">
        <section className="rp-card rp-health">
          <div className="rp-card-hd">
            <h3>Server Health</h3>
            <StatusPill tone={healthTone} label={healthLabel} />
          </div>
          <div className="rp-resource-grid">
            <ResourceBar
              label="CPU"
              percent={cpuPct}
              detail={
                metrics?.cpu?.cores
                  ? `${metrics.cpu.cores} cores`
                  : "—"
              }
            />
            <ResourceBar
              label="RAM"
              percent={ramPct}
              detail={
                metrics?.memory
                  ? `${formatBytes(metrics.memory.usedBytes)} / ${formatBytes(metrics.memory.totalBytes)}`
                  : "—"
              }
            />
            <ResourceBar
              label="Disk"
              percent={diskPct}
              detail={
                metrics?.disk?.[0]
                  ? `${formatBytes(metrics.disk[0].usedBytes)} / ${formatBytes(metrics.disk[0].totalBytes)}`
                  : "—"
              }
            />
          </div>
          <dl className="rp-health-meta">
            <div>
              <dt>Load average</dt>
              <dd className="mono">{loadAvg || "—"}</dd>
            </div>
            <div>
              <dt>Services</dt>
              <dd>{agentOk || isReseller ? "Operational" : "Agent offline"}</dd>
            </div>
            <div>
              <dt>Hostname</dt>
              <dd className="mono">
                {licenseStatus?.hostname || os?.hostname || "—"}
              </dd>
            </div>
            <div>
              <dt>Server IP</dt>
              <dd className="mono">{licenseStatus?.serverIp || "—"}</dd>
            </div>
            <div>
              <dt>OS</dt>
              <dd>
                {os?.prettyName || os?.name || "—"}
              </dd>
            </div>
            <div>
              <dt>Panel</dt>
              <dd className="mono">v{meta?.version || "—"}</dd>
            </div>
          </dl>
        </section>

        <section className="rp-card rp-quick">
          <div className="rp-card-hd">
            <h3>Quick Actions</h3>
          </div>
          <div className="rp-quick-grid">
            {quickActions.map((a) => (
              <button
                key={a.id}
                type="button"
                className="rp-quick-tile"
                onClick={a.run}
              >
                <span className="rp-quick-ico" aria-hidden>
                  {quickIcon(a.id)}
                </span>
                <span className="rp-quick-body">
                  <strong>{a.title}</strong>
                  <small>{a.desc}</small>
                </span>
                <span className="rp-quick-arrow" aria-hidden>
                  →
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* License identity (compact, separate from stats) */}
      <section className="rp-card rp-identity">
        <div className="rp-card-hd">
          <h3>License &amp; Server Identity</h3>
          {onOpenLicenses ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onOpenLicenses}
            >
              View License Details
            </button>
          ) : null}
        </div>
        <div className="rp-identity-grid">
          <IdCell
            label="License key"
            value={
              licenseStatus?.licenseKey
                ? maskKey(licenseStatus.licenseKey)
                : licenseStatus?.installId
                  ? `install ${licenseStatus.installId.slice(0, 8)}…`
                  : "—"
            }
          />
          <IdCell label="Type" value={licenseStatus?.kind || "—"} />
          <IdCell label="Package" value={planLabel || "—"} />
          <IdCell
            label="Server IP"
            value={licenseStatus?.serverIp || "—"}
          />
          <IdCell
            label="Expires"
            value={
              licenseStatus?.expiresAt
                ? formatDate(licenseStatus.expiresAt)
                : "—"
            }
          />
          <IdCell
            label="Engine"
            value={
              agentOk
                ? `Online${agent?.version ? ` · ${String(agent.version).slice(0, 16)}` : ""}`
                : "Offline"
            }
          />
        </div>
      </section>

      {/* Hosting accounts table */}
      <section className="rp-card rp-accounts">
        <div className="rp-card-hd">
          <div>
            <h3>Hosting Accounts</h3>
            <p className="rp-card-sub">
              {seatUsed.toLocaleString()} /{" "}
              {(seatMax || 0).toLocaleString()} accounts
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onNav("accounts")}
          >
            View All Accounts →
          </button>
        </div>
        <div className="rp-table-toolbar">
          <input
            type="search"
            className="rp-table-search"
            placeholder="Search account, domain…"
            value={acctQ}
            onChange={(e) => setAcctQ(e.target.value)}
          />
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as typeof statusFilter)
            }
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        {hostAccounts.length === 0 ? (
          <div className="rp-empty">
            <strong>No Hosting Accounts Yet</strong>
            <p>
              Create your first hosting account to start hosting websites.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onNav("accounts", { createAccount: true })}
            >
              + Create Account
            </button>
          </div>
        ) : (
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Domain</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Disk</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {hostAccounts.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong className="mono">{a.username}</strong>
                    </td>
                    <td className="faint">
                      {a.primaryDomain ||
                        domainByUser.get(a.id) ||
                        "—"}
                    </td>
                    <td>{a.packageName || "—"}</td>
                    <td>
                      <StatusPill
                        tone={
                          a.status === "active"
                            ? "ok"
                            : a.status === "suspended"
                              ? "warn"
                              : "muted"
                        }
                        label={
                          a.status.charAt(0).toUpperCase() +
                          a.status.slice(1)
                        }
                      />
                    </td>
                    <td className="mono faint">
                      {a.diskUsedMb != null
                        ? formatMb(a.diskUsedMb)
                        : "—"}
                    </td>
                    <td className="rp-row-act">
                      <button
                        type="button"
                        className="rp-more"
                        title="Open account list"
                        onClick={() => onNav("accounts")}
                      >
                        •••
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Needs attention */}
      <section className="rp-card rp-alerts">
        <div className="rp-card-hd">
          <h3>Needs Attention</h3>
          <span className="rp-card-count">{alerts.length}</span>
        </div>
        {alerts.length === 0 ? (
          <p className="rp-empty-inline">
            Nothing critical right now. All monitored systems look good.
          </p>
        ) : (
          <ul className="rp-alert-list">
            {alerts.map((a, i) => (
              <li key={i} className={`rp-alert rp-alert-${a.level}`}>
                <span className="rp-alert-level">
                  {a.level === "critical"
                    ? "Critical"
                    : a.level === "warning"
                      ? "Warning"
                      : "Info"}
                </span>
                <div className="rp-alert-body">
                  <strong>{a.title}</strong>
                  <span>{a.detail}</span>
                </div>
                {a.action && a.actionLabel ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={a.action}
                  >
                    {a.actionLabel}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Global search results */}
      {searching ? (
        <section className="rp-card">
          <div className="rp-card-hd">
            <h3>
              Tools matching “{q.trim()}”
              <span className="rp-card-count">{filteredTools.length}</span>
            </h3>
          </div>
          {filteredTools.length === 0 ? (
            <p className="rp-empty-inline">No tools match your search.</p>
          ) : (
            <div className="rp-search-results">
              {filteredTools.slice(0, 24).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="rp-search-item"
                  onClick={() => openTool(t)}
                >
                  <strong>{t.label}</strong>
                  <small>{t.description}</small>
                </button>
              ))}
            </div>
          )}
        </section>
      ) : null}

      {/* Plans count chip for context */}
      {hostPlans.length > 0 ? (
        <p className="rp-foot-meta">
          {hostPlans.length} hosting plan(s) configured ·{" "}
          {sectionsForRole(user.role, authorityMode).length} menu sections
        </p>
      ) : null}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  onClick
}: {
  label: string;
  value: string;
  hint?: string;
  onClick?: () => void;
}) {
  return (
    <button type="button" className="rp-kpi" onClick={onClick}>
      <span className="rp-kpi-label">{label}</span>
      <span className="rp-kpi-value">{value}</span>
      {hint ? <span className="rp-kpi-hint">{hint}</span> : null}
    </button>
  );
}

function ResourceBar({
  label,
  percent,
  detail
}: {
  label: string;
  percent: number | null;
  detail?: string;
}) {
  const p =
    percent == null || !Number.isFinite(percent)
      ? null
      : Math.min(100, Math.max(0, percent));
  const tone =
    p == null ? "muted" : p >= 90 ? "crit" : p >= 75 ? "warn" : "ok";
  return (
    <div className="rp-res">
      <div className="rp-res-top">
        <span className="rp-res-label">{label}</span>
        <span className={`rp-res-pct tone-${tone}`}>
          {p == null ? "—" : `${Math.round(p)}%`}
        </span>
      </div>
      <div className="rp-progress rp-progress-sm">
        <div
          className={`rp-progress-fill tone-${tone}`}
          style={{ width: p == null ? "0%" : `${p}%` }}
        />
      </div>
      <div className="rp-res-detail">
        <span>{detail || "—"}</span>
        <span className={`rp-res-status tone-${tone}`}>
          {p == null ? "n/a" : p >= 90 ? "High" : p >= 75 ? "Elevated" : "Normal"}
        </span>
      </div>
    </div>
  );
}

function StatusPill({
  tone,
  label
}: {
  tone: "ok" | "warn" | "crit" | "muted" | "info";
  label: string;
}) {
  return (
    <span className={`rp-status rp-status-${tone}`}>
      <span className="rp-status-dot" aria-hidden />
      {label}
    </span>
  );
}

function IdCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rp-id-cell">
      <span className="rp-id-label">{label}</span>
      <span className="rp-id-value mono">{value}</span>
    </div>
  );
}

function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n < 10 ? n.toFixed(2) : String(Math.round(n * 100) / 100);
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function formatMb(mb: number): string {
  if (!Number.isFinite(mb)) return "—";
  if (mb < 1024) return `${Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function maskKey(key: string): string {
  if (key.length <= 12) return key;
  return `${key.slice(0, 8)}…${key.slice(-4)}`;
}

function quickIcon(id: string): string {
  const map: Record<string, string> = {
    "create-account": "+",
    "list-accounts": "☰",
    "hosting-plans": "▦",
    "add-domain": "◎",
    mysql: "⛁",
    phpmyadmin: "⛁",
    packages: "▦",
    email: "✉",
    "all-sites": "◎"
  };
  return map[id] || "→";
}
